import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProfileChannel } from "@/types/profile";
import { PROFILE_SIDEBAR_NODE, DEFAULT_PROFILE_CHANNEL_NAME } from "@/constants/profileConstants";
import {
  buildAutoProfileName,
  createDefaultChannel,
  createDefaultOwner,
  createDefaultProfileChannels,
  getNavTargetAfterChannelDelete,
  parseSidebarNodeId,
  normalizeProfilePlatformValues,
  ProfileOwnerFormEntry,
  dedupeOwnerEmails,
  isProfileOwnerLocked,
  withChannelLockedFieldDefaults
} from "@/utilities/profiles/profileFormUtils";
import { MAX_PROFILE_OWNERS, PROFILE_PLATFORM } from "@/constants/profileConstants";
import { getAllChannelNodeIds, stripNodeErrors } from "@/utilities/profiles/profileValidationUtils";

export interface SubmitJobProfileState {
  plc: string;
  profileName: string;
  owners: ProfileOwnerFormEntry[];
  releaseTypes: string[];
  platforms: string[];
  channels: ProfileChannel[];
  activeNodeId: string;
  expandedChannelGroups: string[];
  editMode: boolean;
  editPlc: string;
  editProfileName: string;
  fieldErrors: Record<string, string>;
  validatedNodes: Record<string, boolean>;
  /** Email of the signed-in user; matching owner row cannot be edited or removed. */
  lockedOwnerEmail: string;
  /** Existing profile opened without edit permission — form is view-only. */
  readOnly: boolean;
  /** JSON snapshot from {@link buildProfileFormSaveSnapshot} after load or initial create hydrate. */
  saveBaselineSnapshot: string;
}

const initialState: SubmitJobProfileState = {
  plc: "",
  profileName: "",
  owners: [createDefaultOwner()],
  releaseTypes: ["Product"],
  platforms: [PROFILE_PLATFORM.WINDOWS],
  channels: createDefaultProfileChannels(),
  activeNodeId: PROFILE_SIDEBAR_NODE.GENERAL,
  expandedChannelGroups: ["channel-info"],
  editMode: false,
  editPlc: "",
  editProfileName: "",
  fieldErrors: {},
  validatedNodes: {},
  lockedOwnerEmail: "",
  readOnly: false,
  saveBaselineSnapshot: ""
};

const submitJobProfileSlice = createSlice({
  name: "submitJobProfile",
  initialState,
  reducers: {
    resetSubmitJobProfile: () => ({
      ...initialState,
      channels: createDefaultProfileChannels(),
      fieldErrors: {},
      validatedNodes: {}
    }),
    hydrateSubmitJobProfile: (state, action: PayloadAction<Partial<SubmitJobProfileState>>) => {
      Object.assign(state, action.payload);
    },
    setActiveNodeId: (state, action: PayloadAction<string>) => {
      state.activeNodeId = action.payload;
      const nodeId = action.payload;
      const isChannelNode = nodeId.startsWith(PROFILE_SIDEBAR_NODE.CHANNEL_PREFIX);
      if (isChannelNode && !state.expandedChannelGroups.includes("channel-info")) {
        state.expandedChannelGroups.push("channel-info");
      }
    },
    toggleChannelGroupExpanded: (state, action: PayloadAction<string>) => {
      const group = action.payload;
      if (state.expandedChannelGroups.includes(group)) {
        state.expandedChannelGroups = state.expandedChannelGroups.filter((g) => g !== group);
      } else {
        state.expandedChannelGroups.push(group);
      }
    },
    updateProfileField: (
      state,
      action: PayloadAction<{ key: keyof Pick<SubmitJobProfileState, "plc" | "profileName">; value: string }>
    ) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    updateOwner: (
      state,
      action: PayloadAction<{ index: number; key: keyof ProfileOwnerFormEntry; value: string }>
    ) => {
      const owner = state.owners[action.payload.index];
      if (!owner || isProfileOwnerLocked(owner, state.lockedOwnerEmail)) return;
      owner[action.payload.key] = action.payload.value;
      if (action.payload.key === "email") {
        state.owners = dedupeOwnerEmails(state.owners);
      }
    },
    addOwner: (state) => {
      if (state.owners.length < MAX_PROFILE_OWNERS) {
        state.owners.push(createDefaultOwner());
      }
    },
    removeOwner: (state, action: PayloadAction<number>) => {
      const owner = state.owners[action.payload];
      if (!owner || isProfileOwnerLocked(owner, state.lockedOwnerEmail)) return;
      state.owners.splice(action.payload, 1);
    },
    setReleaseTypes: (state, action: PayloadAction<string[]>) => {
      state.releaseTypes = action.payload;
      state.channels = state.channels.map((channel) =>
        withChannelLockedFieldDefaults(channel, action.payload)
      );
      if (!state.editMode) {
        state.profileName = buildAutoProfileName(state.plc, state.releaseTypes, state.platforms);
      }
    },
    setPlatforms: (state, action: PayloadAction<string[]>) => {
      state.platforms = normalizeProfilePlatformValues(action.payload);
      if (!state.editMode) {
        state.profileName = buildAutoProfileName(state.plc, state.releaseTypes, state.platforms);
      }
    },
    setPlc: (state, action: PayloadAction<string>) => {
      state.plc = action.payload;
      if (!state.editMode) {
        state.profileName = buildAutoProfileName(state.plc, state.releaseTypes, state.platforms);
      }
    },
    setProfileName: (state, action: PayloadAction<string>) => {
      state.profileName = action.payload;
    },
    addChannel: (state, action: PayloadAction<string>) => {
      const name = action.payload.toLowerCase();
      if (state.channels.some((c) => c.Name.toLowerCase() === name)) return;
      state.channels.push(createDefaultChannel(name));
      state.activeNodeId = `channel:${name}`;
      if (!state.expandedChannelGroups.includes("channel-info")) {
        state.expandedChannelGroups.push("channel-info");
      }
    },
    updateChannel: (state, action: PayloadAction<{ name: string; patch: Partial<ProfileChannel> }>) => {
      const idx = state.channels.findIndex((c) => c.Name === action.payload.name);
      if (idx >= 0) {
        state.channels[idx] = withChannelLockedFieldDefaults(
          {
            ...state.channels[idx],
            ...action.payload.patch
          },
          state.releaseTypes
        );
      }
    },
    removeChannel: (state, action: PayloadAction<string>) => {
      const name = action.payload;
      if (name.toLowerCase() === DEFAULT_PROFILE_CHANNEL_NAME) return;

      const channelsBefore = state.channels;
      const nodeIdsBefore = getAllChannelNodeIds(channelsBefore);
      const parsedActive = parseSidebarNodeId(state.activeNodeId);
      const wasActive = parsedActive.name === name;

      state.channels = channelsBefore.filter((c) => c.Name !== name);

      const nodeIdsAfter = new Set(getAllChannelNodeIds(state.channels));
      nodeIdsBefore.forEach((nodeId) => {
        if (nodeIdsAfter.has(nodeId)) return;
        state.fieldErrors = stripNodeErrors(state.fieldErrors, nodeId);
        delete state.validatedNodes[nodeId];
      });

      if (wasActive) {
        state.activeNodeId = getNavTargetAfterChannelDelete(channelsBefore, state.channels, name);
      }
    },
    validateAndMarkNode: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      state.validatedNodes[nodeId] = true;
    },
    setNodeFieldErrors: (
      state,
      action: PayloadAction<{ nodeId: string; errors: Record<string, string> }>
    ) => {
      state.fieldErrors = {
        ...stripNodeErrors(state.fieldErrors, action.payload.nodeId),
        ...action.payload.errors
      };
    },
    clearFieldError: (state, action: PayloadAction<string>) => {
      delete state.fieldErrors[action.payload];
    }
  }
});

export const {
  resetSubmitJobProfile,
  hydrateSubmitJobProfile,
  setActiveNodeId,
  toggleChannelGroupExpanded,
  updateProfileField,
  updateOwner,
  addOwner,
  removeOwner,
  setReleaseTypes,
  setPlatforms,
  setPlc,
  setProfileName,
  addChannel,
  updateChannel,
  removeChannel,
  validateAndMarkNode,
  setNodeFieldErrors,
  clearFieldError
} = submitJobProfileSlice.actions;

export default submitJobProfileSlice.reducer;
